/**
 * Cortex Context Enhancement Mycelium Connector
 *
 * Enhances AI context and Quick Actions filtering based on real-time user state.
 * Integrates with Cortex service to provide context-aware AI assistance.
 *
 * Key Features:
 * - Filters Quick Actions by user role/permissions/expertise
 * - Updates AI context when user state changes
 * - Tracks system state for intelligent suggestions
 * - Dynamic action visibility based on actual capabilities
 * - Real-time context updates to Cortex service
 *
 * Flow Example:
 * 1. User promoted to Manager role
 *    → role_changed event published
 *    → Connector updates context
 *    → Quick Actions filtered to show management actions
 *    → Cortex AI receives updated capabilities
 *
 * @module mycelium-cortex-context
 * @since 2026-02-20
 */

import {
  UserContextEnrichedPayloadSchema,
  UserContextUpdatedPayloadSchema,
} from '@flowstate/mycelium-core';
import type { MyceliumEvent } from './mycelium-event-backbone.service';
import { myceliumEventBackbone } from './mycelium-event-backbone.service';
import { getMyceliumTransport } from './mycelium-transport-registry';
import { logger } from '../lib/logger';
import { ROLE_PERMISSIONS, resolvePermissions, type Permission } from '@flowstate/types';
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * User roles and their capabilities — derived from shared ROLE_PERMISSIONS.
 * Maps permission strings to Quick Action identifiers for the UI.
 */
const PERMISSION_TO_ACTION: Record<string, string> = {
  'manage:users': 'manage_users',
  'manage:settings': 'manage_settings',
  'manage:billing': 'manage_billing',
  'view:all_data': 'view_analytics',
  'manage:team': 'manage_team',
  'view:team_analytics': 'view_team_analytics',
  'approve:requests': 'approve_requests',
  'view:all_tasks': 'view_all_tasks',
  'create:tasks': 'create_task',
  'edit:own_tasks': 'edit_my_tasks',
  'create:meetings': 'create_meeting',
  'manage:bookings': 'manage_bookings',
};

/**
 * Context update triggers
 */
const CONTEXT_TRIGGERS = [
  'user_role_changed',
  'permission_granted',
  'permission_revoked',
  'team_assigned',
  'team_removed',
  'organization_joined',
  'organization_left',
  'feature_enabled',
  'feature_disabled',
] as const;

// ============================================================================
// TYPES
// ============================================================================

interface UserContext {
  userId: string;
  role: string;
  permissions: string[];
  teams: string[];
  organizationId?: string;
  features: string[];
  lastUpdated: string;
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class CortexContextEnhancementConnector {
  private initialized = false;
  private userContextCache = new Map<string, UserContext>();

  /**
   * Initialize connector and subscribe to events
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("CortexContextEnhancementConnector already initialized");
      return;
    }

    logger.info("🧠 Cortex Context Enhancement connector initializing");

    // Subscribe to user domain events
    myceliumEventBackbone.subscribe({
      name: "CortexContextEnhancementConnector-User",
      domains: ["user", "system"],
      eventTypes: [...CONTEXT_TRIGGERS],
      handler: this.handleContextChangeEvent.bind(this),
      priority: 8,
    });

    // Subscribe to AI/Quick Actions requests
    myceliumEventBackbone.subscribe({
      name: "CortexContextEnhancementConnector-AI",
      domains: ["ai"],
      eventTypes: ["quick_actions_requested", "ai_context_requested"],
      handler: this.handleContextRequest.bind(this),
      priority: 9, // High priority to provide context before AI processes
    });

    this.initialized = true;
    logger.info("✅ Cortex Context Enhancement connector ready");
    logger.info("💡 Quick Actions will be filtered by user context");
  }

  /**
   * Handle context change events
   */
  private async handleContextChangeEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    if (!userId) {
      return;
    }

    logger.debug("🔄 User context change detected", {
      eventId: event.id,
      type,
      userId,
    });

    try {
      // Fetch updated user context
      const updatedContext = await this.fetchUserContext(userId);

      if (!updatedContext) {
        logger.warn("Could not fetch user context", { userId });
        return;
      }

      // Update cache
      this.userContextCache.set(userId, updatedContext);

      logger.info("✅ User context updated", {
        userId,
        role: updatedContext.role,
        permissions: updatedContext.permissions.length,
        teams: updatedContext.teams.length,
      });

      const contextUpdatedPayload = {
        context: updatedContext,
        previousEvent: event.id,
        updatedAt: new Date().toISOString(),
      };
      const contextUpdatedCheck = UserContextUpdatedPayloadSchema.safeParse(contextUpdatedPayload);
      if (!contextUpdatedCheck.success) {
        logger.warn("Skipping user_context_updated — payload schema mismatch", {
          userId,
          issues: contextUpdatedCheck.error.issues.map((i) => i.message),
        });
      } else {
        await getMyceliumTransport().publish({
          domain: "ai",
          type: "user_context_updated",
          userId,
          payload: contextUpdatedCheck.data,
        });
      }

      // ── Phase 5.1: Publish user_context_enriched event ──────────────
      const enrichedPayload = {
        cortexContext: {
          userRole: updatedContext.role,
          permissions: updatedContext.permissions,
          teams: updatedContext.teams,
          features: updatedContext.features,
          enrichedAt: new Date().toISOString(),
        },
        sessionId: undefined,
        source: "cortex-context-connector",
        trigger: {
          eventType: type,
          eventId: event.id,
        },
      };
      const enrichedCheck = UserContextEnrichedPayloadSchema.safeParse(enrichedPayload);
      if (!enrichedCheck.success) {
        logger.warn("Skipping user_context_enriched — payload schema mismatch", {
          userId,
          issues: enrichedCheck.error.issues.map((i) => i.message),
        });
      } else {
        await getMyceliumTransport().publish({
          domain: "ai",
          type: "user_context_enriched",
          userId,
          payload: enrichedCheck.data,
        });
      }

      // Publish filtered_actions_available event
      const filteredActions = this.filterQuickActions(updatedContext);
      await getMyceliumTransport().publish({
        domain: "ai",
        type: "filtered_actions_available",
        userId,
        payload: {
          actions: filteredActions,
          context: updatedContext,
        },
      });
    } catch (error) {
      logger.error("Error handling context change", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Handle context request events (real-time)
   */
  private async handleContextRequest(event: MyceliumEvent): Promise<void> {
    const { type, userId } = event;

    if (!userId) {
      return;
    }

    logger.debug("📋 Context requested", { eventId: event.id, type, userId });

    try {
      // Check cache first
      let context = this.userContextCache.get(userId);

      // If not cached or stale (>5 min), fetch fresh
      if (!context || this.isContextStale(context)) {
        context = await this.fetchUserContext(userId);

        if (context) {
          this.userContextCache.set(userId, context);
        }
      }

      if (!context) {
        logger.warn("No context available for user", { userId });
        return;
      }

      // Respond with context
      await getMyceliumTransport().publish({
        domain: "ai",
        type: "context_response",
        userId,
        payload: {
          context,
          requestEventId: event.id,
          respondedAt: new Date().toISOString(),
        },
      });
    } catch (error) {
      logger.error("Error handling context request", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Fetch user context from database
   */
  private async fetchUserContext(userId: string): Promise<UserContext | undefined> {
    try {
      // Fetch user profile with roles and permissions
      const { data: profile, error } = await typedSupabase
        .from('profiles')
        .select('id, role, organization_id')
        .eq('id', userId)
        .single();

      if (error || !profile) {
        return undefined;
      }

      // Resolve permissions from shared ROLE_PERMISSIONS
      const role = profile.role || 'member';
      const permissions = resolvePermissions([role]);

      // Fetch user teams (simplified)
      const { data: teams } = await typedSupabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', userId);

      const teamIds = teams?.map(t => t.team_id) || [];

      return {
        userId: profile.id,
        role,
        permissions: permissions as string[],
        teams: teamIds,
        organizationId: profile.organization_id,
        features: [], // Would fetch enabled features
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      logger.error("Error fetching user context", { userId, error });
      return undefined;
    }
  }

  /**
   * Filter Quick Actions based on user context
   * Uses shared ROLE_PERMISSIONS instead of local hardcoded mapping
   */
  private filterQuickActions(context: UserContext): string[] {
    const { role } = context;

    // Base actions available to everyone
    const baseActions = [
      'view_dashboard',
      'create_task',
      'view_my_tasks',
      'view_calendar',
    ];

    // Resolve permissions from shared ROLE_PERMISSIONS
    const permissions = resolvePermissions([role]);

    // Map permissions to Quick Action identifiers
    const actionSet = new Set(baseActions);
    for (const perm of permissions) {
      const action = PERMISSION_TO_ACTION[perm];
      if (action) {
        actionSet.add(action);
      }
    }

    // Admin-specific UI actions not tied to a single permission
    if (role === 'admin') {
      actionSet.add('manage_teams');
    }

    return Array.from(actionSet);
  }

  /**
   * Check if context is stale (>5 minutes)
   */
  private isContextStale(context: UserContext): boolean {
    const lastUpdated = new Date(context.lastUpdated);
    const now = new Date();
    const diffMs = now.getTime() - lastUpdated.getTime();
    return diffMs > 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Cleanup on disposal
   */
  async dispose(): Promise<void> {
    this.userContextCache.clear();
    this.initialized = false;
    logger.info("🧹 Cortex Context Enhancement connector disposed");
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const cortexContextEnhancementConnector = new CortexContextEnhancementConnector();
